import { Component, signal, effect, computed, ViewChild, ElementRef, OnDestroy, AfterViewInit, EffectRef, OnInit, untracked, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { NgFor, NgIf, NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { PortfolioItem } from './PortfolioItem';
import { PortfolioCategory } from './PortfolioCategory';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [NgFor, NgIf, NgClass, RouterLink],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements AfterViewInit, OnDestroy, OnInit {
  private static readonly LIGHT_FAVICON = 'favicon-light.ico';
  private static readonly DARK_FAVICON = 'favicon-dark.ico';

  year = new Date().getFullYear();
  menuOpen = signal(false);
  isLoading = signal(true);
  loadError = signal<string | null>(null);
  // Soft password gate
  // locked = signal(true);
  locked = signal(false); // temporarily disable password gate
  passwordError = signal<string | null>(null);
  // Set to the SHA-256 hex of your password
  private static readonly PASSWORD_HASH_HEX = '78e51b4eb97dd026dac7cc9746f73eda83dc8bbad1b718e0655c2a9be75cd0fc';

  // Theme state
  theme = signal<'light' | 'dark'>(
    (() => {
      try {
        const saved = localStorage.getItem('theme') as 'light' | 'dark' | null;
        if (saved === 'light' || saved === 'dark') return saved;
      } catch {}
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    })()
  );

  // Items will be loaded from assets JSON at runtime
  items = signal<PortfolioItem[]>([]);

  // Category tab state and derived values
  selectedCategory = signal<'All' | PortfolioCategory>('All');
  categories = computed(() => {
    const cats = Array.from(new Set<PortfolioCategory>(this.items().map(i => i.category)));
    return [...cats, 'All'] as Array<'All' | PortfolioCategory>;
  });
  displayedItems = computed(() =>
    this.selectedCategory() === 'All'
      ? this.items()
      : this.items().filter(i => i.category === this.selectedCategory())
  );

  // Paging signals and derived values
  pageSize = signal(9);
  visibleCount = signal(this.pageSize());
  pagedItems = computed(() => this.displayedItems().slice(0, this.visibleCount()));
  hasMore = computed(() => this.displayedItems().length > this.visibleCount());

  // Lazy loading (IntersectionObserver)
  @ViewChild('sentinel') sentinel?: ElementRef<HTMLDivElement>;
  private io?: IntersectionObserver;
  private resetEffectRef?: EffectRef;
  private observeEffectRef?: EffectRef;
  private lastObservedEl: Element | null = null;
  private isObserving = false;
  private lastAutoLoad = 0; // timestamp for IO triggered load
  private autoLoadCooldownMs = 400; // debounce window

  constructor(private http: HttpClient, private envInjector: EnvironmentInjector) {
    this.loadItems();

    // Reset paging when category or items change
    this.resetEffectRef = effect(() => {
      // dependencies (reactive reads)
      const _cat = this.selectedCategory();
      const _items = this.items();
      const desired = this.pageSize();
      // Do not track visibleCount here, otherwise loadMore() updates retrigger this
      // effect and immediately reset paging back to pageSize.
      const current = untracked(() => this.visibleCount());
      if (current !== desired) {
        untracked(() => this.visibleCount.set(desired));
      }
    });

    // Reflect theme to <html data-theme="...">
    effect(() => {
      const t = this.theme();
      document.documentElement.setAttribute('data-theme', t);
      this.updateFavicon(t);
      try { localStorage.setItem('theme', t); } catch {}
    });

    // Restore unlock state (temporarily disabled while password gate is off)
    // try { if (localStorage.getItem('site-unlocked') === '1') this.locked.set(false); } catch {}
  }

  ngAfterViewInit(): void {
    this.io = new IntersectionObserver((entries) => {
      if (!this.hasMore()) return;
      if (entries.some(e => e.isIntersecting)) {
        const now = performance.now();
        if (now - this.lastAutoLoad > this.autoLoadCooldownMs) {
          this.lastAutoLoad = now;
          this.loadMore();
        }
      }
    }, { rootMargin: '200px' });

    // Observe/unobserve sentinel as state changes
    runInInjectionContext(this.envInjector, () => {
      this.observeEffectRef = effect(() => {
        // Read dependencies reactively
        const el = this.sentinel?.nativeElement || null;
        const canObserve = !!el && !this.isLoading() && !this.loadError() && this.hasMore();

        if (el === this.lastObservedEl && canObserve === this.isObserving) {
          return;
        }
        untracked(() => {
          if (this.isObserving) {
            try { this.io?.unobserve(this.lastObservedEl as Element); } catch {}
            this.isObserving = false;
          }
          this.lastObservedEl = el;
          if (canObserve && el) {
            this.io?.observe(el);
            this.isObserving = true;
          }
        });
      });
    });
  }

  private updateFavicon(theme: 'light' | 'dark') {
    const head = document.head || document.getElementsByTagName('head')[0];
    const favicon = document.getElementById('app-favicon');
    const next = document.createElement('link');
    next.id = 'app-favicon';
    next.rel = 'icon';
    next.type = 'image/x-icon';
    next.href = `${theme === 'dark' ? AppComponent.DARK_FAVICON : AppComponent.LIGHT_FAVICON}?v=${theme}`;

    if (favicon?.parentNode) {
      favicon.parentNode.removeChild(favicon);
    }

    head.appendChild(next);
  }

  ngOnDestroy(): void {
    if (this.isObserving && this.lastObservedEl) {
      try { this.io?.unobserve(this.lastObservedEl); } catch {}
    }
    this.io?.disconnect();
    this.observeEffectRef?.destroy();
    this.resetEffectRef?.destroy();
    if (this.transitionTimer) clearTimeout(this.transitionTimer);
    this.setLightboxScrollLock(false);
    window.removeEventListener('keydown', this.keyHandler);
  }

  ngOnInit(): void {
    window.addEventListener('keydown', this.keyHandler);
  }

  private loadItems() {
    this.isLoading.set(true);
    this.loadError.set(null);
    this.http.get<any[]>('assets/portfolio-items.json').subscribe({
      next: (raw) => {
        // Map category string to enum value and enforce typing.
        // Value-based validation supports categories like "Range-Time".
        const validCategories = new Set(Object.values(PortfolioCategory));
        const mapped = raw.map((i) => ({
          ...i,
          category: validCategories.has(i.category)
            ? (i.category as PortfolioCategory)
            : PortfolioCategory.General
        })) as PortfolioItem[];
        // Sanitize image paths defensively (avoid malformed src/full causing URL errors in some environments)
        mapped.forEach(it => {
          const sFull = this.sanitizeImagePath(it.full);
          if (sFull) it.full = sFull;
          const sSrc = this.sanitizeImagePath(it.src);
          if (sSrc) it.src = sSrc;
        });
        this.items.set(mapped);
        // Default to first category once data is available (instead of 'All')
        if (mapped.length && this.selectedCategory() === 'All') {
          this.selectedCategory.set(mapped[0].category);
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Failed to load portfolio items', err);
        this.loadError.set('Unable to load portfolio items. Please try again.');
        this.isLoading.set(false);
      }
    });
  }

  lightboxOpen = signal(false);
  lightboxSrc = signal<string>('');
  lightboxLoading = signal(false);
  lightboxIndex = signal<number | null>(null);
  currentLightboxItem = computed(() => {
    const idx = this.lightboxIndex();
    const list = this.displayedItems();
    if (idx == null || idx < 0 || idx >= list.length) return null;
    return list[idx];
  });

  toggleMenu() { this.menuOpen.update(v => !v); }

  openLightbox(index: number) {
    const list = this.displayedItems();
    if (index < 0 || index >= list.length) return;
    this.lightboxOpen.set(true);
    this.lightboxLoading.set(true);
    this.lightboxIndex.set(index);
    const rawPath = list[index].full || list[index].src;
    const safe = this.sanitizeImagePath(rawPath);
    if (!safe) {
      console.warn('Refusing to open lightbox for item with invalid image path', rawPath, list[index]);
      this.lightboxLoading.set(false);
      this.setLightboxScrollLock(true);
      return;
    }
    this.preloadLightboxImage(safe, index, null);
    this.setLightboxScrollLock(true);
  }

  closeLightbox() {
    this.lightboxOpen.set(false);
    this.lightboxSrc.set('');
    this.lightboxLoading.set(false);
    this.lightboxIndex.set(null);
    if (this.transitionTimer) {
      clearTimeout(this.transitionTimer);
      this.transitionTimer = null;
    }
    this.animating.set(false);
    this.transitionDirection.set(null);
    this.setLightboxScrollLock(false);
  }

  trackByIndex = (_: number, __: PortfolioItem) => _;
  trackBySrc = (_: number, item: PortfolioItem) => item.src;
  trackByString = (_: number, s: string) => s;

  onImgLoad(event: Event) {
    const img = event.target as HTMLImageElement;
    img.classList.add('loaded');
  }

  selectCategory(cat: 'All' | PortfolioCategory) {
    if (this.selectedCategory() === cat) return;
    this.gridVisible.set(false);
    setTimeout(() => {
      this.selectedCategory.set(cat);
      this.gridVisible.set(true);
    }, 150);
  }

  loadMore() {
    // Guard: only act if we truly have more items
    const current = this.visibleCount();
    const total = this.displayedItems().length;
    if (current >= total) return;
    const next = Math.min(current + this.pageSize(), total);
    this.visibleCount.set(next);
  }

  toggleTheme() {
    this.theme.update(t => (t === 'dark' ? 'light' : 'dark'));
  }

  nextImage() {
    if (!this.lightboxOpen()) return;
    const list = this.displayedItems();
    const idx = this.lightboxIndex();
    if (idx == null) return;
    const next = (idx + 1) % list.length;
    this.updateImageWithAnimation(next, 'next');
  }

  prevImage() {
    if (!this.lightboxOpen()) return;
    const list = this.displayedItems();
    const idx = this.lightboxIndex();
    if (idx == null) return;
    const prev = (idx - 1 + list.length) % list.length;
    this.updateImageWithAnimation(prev, 'prev');
  }

  async submitPassword(pw: string) {
    this.passwordError.set(null);
    try {
      const enteredHash = await this.sha256Hex(pw);
      if (enteredHash === AppComponent.PASSWORD_HASH_HEX) {
        this.locked.set(false);
        try { localStorage.setItem('site-unlocked', '1'); } catch {}
      } else {
        this.passwordError.set('Incorrect password.');
      }
    } catch (e) {
      console.error('Hashing failed', e);
      this.passwordError.set('Unexpected error. Please try again.');
    }
  }

  private async sha256Hex(input: string): Promise<string> {
    const enc = new TextEncoder();
    const data = enc.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }
  
  private keyHandler = (e: KeyboardEvent) => {
    if (!this.lightboxOpen()) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); this.nextImage(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); this.prevImage(); }
    else if (e.key === 'Escape') { this.closeLightbox(); }
  };

  // Grid visibility for category-switch fade
  gridVisible = signal(true);

  // Animation + swipe support
  transitionDirection = signal<'next' | 'prev' | null>(null);
  animating = signal(false);
  private static readonly TRANSITION_MS = 450; // keep in sync with the CSS animation duration
  private lightboxRequestId = 0;
  private transitionTimer: ReturnType<typeof setTimeout> | null = null;
  private swipeStartX: number | null = null;
  private swipeStartY: number | null = null;
  private swipeThreshold = 40; // px horizontal travel
  private maxVerticalDrift = 60;

  onTouchStart(e: TouchEvent) {
    if (!this.lightboxOpen()) return;
    const t = e.touches[0];
    this.swipeStartX = t.clientX;
    this.swipeStartY = t.clientY;
  }
  onTouchMove(e: TouchEvent) {
    // Could implement drag preview later
  }
  onTouchEnd(e: TouchEvent) {
    if (this.swipeStartX == null || this.swipeStartY == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - this.swipeStartX;
    const dy = t.clientY - this.swipeStartY;
    if (Math.abs(dx) > this.swipeThreshold && Math.abs(dy) < this.maxVerticalDrift) {
      if (dx < 0) this.nextImage(); else this.prevImage();
    }
    this.swipeStartX = this.swipeStartY = null;
  }

  private updateImageWithAnimation(newIndex: number, direction: 'next' | 'prev') {
    const list = this.displayedItems();
    const item = list[newIndex];
    if (!item) return;
    const rawPath = item.full || item.src;
    const safe = this.sanitizeImagePath(rawPath);
    if (!safe) {
      console.warn('Skipping lightbox image update due to invalid path', rawPath, item);
      return;
    }
    // Note: the current image stays visible (no lightboxLoading hide) while the
    // next one preloads, so nothing flickers or blanks out during the fetch.
    this.preloadLightboxImage(safe, newIndex, direction);
  }

  private preloadLightboxImage(src: string, index: number, direction: 'next' | 'prev' | null) {
    if (!this.lightboxOpen()) return;
    const requestId = ++this.lightboxRequestId;
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      if (!this.lightboxOpen() || requestId !== this.lightboxRequestId) return;
      this.lightboxIndex.set(index);
      this.lightboxSrc.set(src);
      this.lightboxLoading.set(false);
      if (direction) this.playTransition(direction);
    };
    img.onerror = () => {
      if (!this.lightboxOpen() || requestId !== this.lightboxRequestId) return;
      this.lightboxLoading.set(false);
    };
    img.src = src;
  }

  // Plays the slide/fade CSS animation on a fixed timeline of its own, starting
  // only once the new image is actually in the DOM — never while still waiting
  // on the network — so the animation and the image swap are never out of sync.
  private playTransition(direction: 'next' | 'prev') {
    if (this.transitionTimer) {
      clearTimeout(this.transitionTimer);
      this.transitionTimer = null;
    }
    const start = () => {
      this.transitionDirection.set(direction);
      this.animating.set(true);
      this.transitionTimer = setTimeout(() => {
        this.animating.set(false);
        this.transitionDirection.set(null);
        this.transitionTimer = null;
      }, AppComponent.TRANSITION_MS);
    };
    if (this.animating()) {
      // Force the CSS animation to restart cleanly instead of jumping mid-keyframe.
      this.animating.set(false);
      requestAnimationFrame(start);
    } else {
      start();
    }
  }

  // Attach key events
  // Keep near bottom so constructor earlier logic stays readable
  // (We can't put in constructor earlier in patch to minimize diff complexity)
  constructor2?: void; // no-op placeholder to anchor comment

  // Helper to defensively sanitize image paths. Returns null if unusable.
  private setLightboxScrollLock(locked: boolean) {
    const root = document.documentElement;
    const body = document.body;
    if (locked) {
      root.classList.add('lightbox-open');
      body.classList.add('lightbox-open');
    } else {
      root.classList.remove('lightbox-open');
      body.classList.remove('lightbox-open');
    }
  }

  private sanitizeImagePath(path: any): string | null {
    if (typeof path !== 'string') return null;
    let p = path.trim();
    if (!p) return null;
    // Allow absolute HTTP(S) URLs as-is
    if (/^https?:\/\//i.test(p)) return p;
    // Remove leading slashes
    p = p.replace(/^\/+/, '');
    // Ensure it is rooted under assets/
    if (!p.toLowerCase().startsWith('assets/')) {
      p = 'assets/' + p;
    }
    return p;
  }
  
  
}
